import React from 'react';
import { motion } from 'framer-motion';
import { useRaceEngineContext } from '../RaceEngine/RaceEngineContext';

/**
 * RacePlannerFilters — Controles de preferencias para el algoritmo de auto-ruta.
 * Estilos: Glassmorphism Masterpiece (mismo sistema que RacePlannerTimeline).
 */
const RacePlannerFilters = () => {
    const { filterPreferences, setFilterPreferences } = useRaceEngineContext();

    const summerEnabled = filterPreferences?.summerVacationEnabled ?? false;
    const maxTriples    = filterPreferences?.maxTriples ?? 0;

    const toggle = (key, value) =>
        setFilterPreferences({ ...filterPreferences, [key]: value });

    return (
        <motion.section
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="w-full bg-white/40 dark:bg-[#0B0F19]/40 backdrop-blur-xl border border-slate-200/50 dark:border-white/5 rounded-3xl p-6 shadow-xl dark:shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
        >
            {/* Header */}
            <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-200/60 dark:border-white/5">
                <span className="text-[10px] font-black uppercase tracking-[0.2em] px-3 py-1 rounded-sm border bg-slate-100/80 dark:bg-white/5 text-slate-500 dark:text-white/40 border-slate-200/50 dark:border-white/10">
                    Route Preferences
                </span>
                <p className="text-[10px] text-slate-400 dark:text-white/25 tracking-widest uppercase">
                    Ajusta el comportamiento del algoritmo
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* ── Control 1: Toggle de Vacaciones de Verano ────────── */}
                <div className="flex items-start justify-between gap-4 p-4 rounded-xl bg-white/30 dark:bg-white/[0.03] border border-slate-200/40 dark:border-white/[0.06]">
                    <div className="flex-1 min-w-0">
                        <p className="text-sm font-black text-slate-700 dark:text-white/80 tracking-tight">
                            Bloquear JUL y AUG
                        </p>
                        <p className="text-[11px] text-slate-400 dark:text-white/30 mt-1 leading-snug">
                            Prioriza el entrenamiento en temporada de verano
                        </p>
                    </div>
                    {/* Toggle */}
                    <button
                        onClick={() => toggle('summerVacationEnabled', !summerEnabled)}
                        aria-pressed={summerEnabled}
                        className={`relative mt-0.5 w-11 h-6 shrink-0 rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#48E5B9]/60 ${
                            summerEnabled
                                ? 'bg-[#48E5B9]'
                                : 'bg-slate-300 dark:bg-white/10'
                        }`}
                    >
                        <motion.span
                            layout
                            transition={{ type: 'spring', stiffness: 700, damping: 40 }}
                            className={`absolute top-0.5 w-5 h-5 rounded-full shadow-md ${
                                summerEnabled
                                    ? 'left-[calc(100%-1.375rem)] bg-white'
                                    : 'left-0.5 bg-white dark:bg-white/70'
                            }`}
                        />
                    </button>
                </div>

                {/* ── Control 2: Selector de Carreras Triples ──────────── */}
                <div className="p-4 rounded-xl bg-white/30 dark:bg-white/[0.03] border border-slate-200/40 dark:border-white/[0.06]">
                    <p className="text-sm font-black text-slate-700 dark:text-white/80 tracking-tight mb-1">
                        Carreras Triples
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-white/30 mb-4 leading-snug">
                        Máximo de secuencias de 3 carreras consecutivas permitidas
                    </p>
                    <input
                            type="number"
                            min={0}
                            value={maxTriples}
                            onChange={(e) => toggle('maxTriples', Math.max(0, Number(e.target.value)))}
                            className="w-24 py-2 px-3 rounded-lg text-center font-black text-slate-700 dark:text-white/80 bg-white/40 dark:bg-white/[0.05] border border-slate-200/50 dark:border-white/10 focus:outline-none focus:border-[#FAB0C0]/60 dark:focus:border-[#FAB0C0]/40 transition-colors"
                        />
                    <p className="text-[10px] text-slate-400 dark:text-white/20 mt-2 tracking-wide">
                        {maxTriples === 0 ? 'Sin triples (mayor descanso)' : `Hasta ${maxTriples} triple${maxTriples > 1 ? 's' : ''}`}
                    </p>
                </div>
            </div>
        </motion.section>
    );
};

export default RacePlannerFilters;
